import {
  GetBolttechVehicleMakersOperation,
  GetBolttechVehiclesOperation,
  GetCarBrandsOperation,
  GetCarFairMarketValueOperation,
  GetCarInsuranceQuoteOperation,
  GetCarModelsOperation,
  GetCarTrimsOperation,
  GetCarYearsOperation,
  Usage,
} from '__generated__/graphql';
import {
  createLazyQuery,
  EmptyQueryArgs,
} from 'car-insurance/hooks/useLazyQuery';
import { VehicleModel, VehicleTrim } from 'car-insurance/types';
import {
  IVehicleDomainService,
  IVehicleMaker,
} from '../../../../../../car-insurance/api/client/clientDomainServices';

export type FairMarketValue = {
  carId: number;
  fmv: number;
};

export interface IFairMarketClient {
  getVehicleFairMarketValue(
    makeName: string,
    modelName: string,
    year: number,
    trim: string,
  ): Promise<FairMarketValue>;
}

/**
 * Domain service for managing vehicle-related data operations.
 * Data access layer for vehicle models and trims
 * MoneyMax specific implementations (Copied from Product-new Service)
 */
export class CmsServiceClientVehiclesDomainService
  implements IVehicleDomainService, IFairMarketClient
{
  public async getVehiclesData({
    makeName,
    useBolttechData = false,
  }: {
    makeName: string;
    useBolttechData: boolean;
  }): Promise<VehicleModel[]> {
    if (useBolttechData) {
      try {
        const query = createLazyQuery(GetBolttechVehiclesOperation);
        const response = await query({
          makeId: makeName,
        });
        return response?.bolttechVehicleModels as VehicleModel[];
      } catch (error) {
        throw new Error('Failed to fetch vehicle');
      }
    }

    try {
      const query = createLazyQuery(GetCarModelsOperation);
      const response = await query({
        brand: makeName,
      });

      return (
        response?.getCarModels?.map((x) => ({
          baseModelName: undefined,
          carrierModelId: undefined,
          makeName: makeName,
          mgfYear: undefined,
          modelId: x ?? '',
          modelName: x ?? '',
          trim: undefined,
        })) ?? []
      );
    } catch (error) {
      console.error(error);

      throw new Error('Failed to fetch vehicle');
    }
  }

  public async getVehicleTrims({
    makeId,
    modelId,
    year,
  }: {
    makeId: string;
    modelId: string;
    year: number;
  }): Promise<VehicleTrim[]> {
    try {
      const query = createLazyQuery(GetCarTrimsOperation);
      const response = await query({
        brand: makeId,
        model: modelId,
        year,
      });
      return (
        response?.getCarTrims?.map((t) => ({
          modelId: '',
          trim: t ?? '',
        })) ?? []
      );
    } catch (error) {
      console.error(error);

      throw new Error('Failed to fetch vehicle variants');
    }
  }

  public async getModelYears(
    brandName: string,
    modelName: string,
  ): Promise<number[]> {
    try {
      const query = createLazyQuery(GetCarYearsOperation);
      const response = await query({
        brand: brandName,
        model: modelName,
      });

      if (!response?.getCarYears) {
        return [];
      }

      return response.getCarYears
        .map((year) => (year ? parseInt(year, 10) : NaN))
        .filter(Boolean);
    } catch (error) {
      console.error(error);

      throw new Error('Failed to fetch vehicle years');
    }
  }

  public async getVehicleMakes({
    useBolttechData,
  }: {
    useBolttechData: boolean;
  }): Promise<IVehicleMaker[]> {
    if (useBolttechData) {
      try {
        const query = createLazyQuery(GetBolttechVehicleMakersOperation);
        const response = await query({} as Parameters<typeof query>[0]);
        return (
          (response?.bolttechVehicleMakers as {
            id: string;
            name: string;
          }[]) ?? []
        );
      } catch (error) {
        throw new Error('Failed to fetch Bolttech vehicle makers');
      }
    }

    try {
      const query = createLazyQuery(GetCarBrandsOperation);
      const response = await query(EmptyQueryArgs);

      return (
        response?.getCarBrands?.map((x) => ({
          id: x ?? '',
          name: x ?? '',
        })) ?? []
      );
    } catch (error) {
      console.error(error);
      throw new Error('Failed to fetch vehicle makers');
    }
  }

  public async getVehicleFairMarketValue(
    makeName: string,
    modelName: string,
    year: number,
    trim: string,
  ): Promise<FairMarketValue> {
    const query = createLazyQuery(GetCarFairMarketValueOperation);
    const response = await query({
      brand: makeName,
      model: modelName,
      year,
      trim,
    });

    return {
      carId: response?.getCarFairMarketValue?.carId || 0,
      fmv: response?.getCarFairMarketValue?.fairMarketValue || 0,
    };
  }

  public async getCarInsurancePremiums(
    // vehicle
    makeName: string,
    modelName: string,
    year: number,
    trim: string,
    // ncd
    bodilyInjuryAndPropertyDamageCoverage: number,
    personalAccidentCoverage: number,
    includesActsOfNature: boolean,
    usage: string,
  ) {
    const query = createLazyQuery(GetCarInsuranceQuoteOperation);

    const fmv = await this.getVehicleFairMarketValue(
      makeName,
      modelName,
      year,
      trim,
    );

    // For some reason the API slices the last two digits
    const propertyDamageCoverage = Number(
      `${bodilyInjuryAndPropertyDamageCoverage}00`,
    );

    const response = await query({
      request: {
        carId: Number(fmv.carId),
        fairMarketValue: fmv.fmv,
        personalAccidentCoverage: personalAccidentCoverage,
        bodilyInjuryAndPropertyDamageCoverage: propertyDamageCoverage,
        includesActsOfNature: includesActsOfNature,
        usage: usage.toUpperCase() as Usage,
        loanWithBankId: 0,
      },
    });

    return response;
  }
}
